<template>
  <div>
    <popup-select-version ref="popupSelectVersion" />
    <vs-prompt
      @cancel="createCopyPromptActive = false"
      @close="createCopyPromptActive = false"
      @accept="createCopy"
      title="Copy list"
      accept-text="Copy list"
      :active.sync="createCopyPromptActive"
    >
      <div>
        <vs-input class="w-full" placeholder="Marketing Review" label="New list display title" v-model="newListTitle" />
        <vs-input
          class="mt-2 w-full"
          placeholder="MarketingReview"
          label="New list type - cannot be changed after creation"
          @keydown.space.prevent
          v-model="newListType"
        />
        <vs-select v-model="newListCountry" class="w-full mt-2" label="New list country">
          <vs-select-item :key="index" :value="item.id.toUpperCase()" :text="item.name" v-for="(item, index) in countries" />
        </vs-select>
      </div>
    </vs-prompt>

    <vs-prompt
      @cancel="revertPromptActive = false"
      @close="revertPromptActive = false"
      @accept="revertToThisVersion"
      title="Revert to this version"
      accept-text="Revert &amp; publish"
      color="warning"
      :active.sync="revertPromptActive"
    >
      <div>
        <p>
          This creates a new version (v{{ newestVersion + 1 }}) that is an exact copy of
          <strong>version {{ list.version }}</strong> and publishes it immediately, making it the active
          list used in the app.
        </p>
        <p class="mt-2">Existing submissions are not affected. This cannot be undone automatically.</p>
      </div>
    </vs-prompt>

    <vx-card no-shadow>
      <div class="vx-row mb-6 mt-2">
        <div class="vx-col sm:w-4/5 w-full">
          <vs-row>
            <h2 style="font-size: revert; font-weight: 500">
              {{ list.title }}
            </h2>
            <a
              :style="{ color: isNewestVersion ? '#7367f0' : '#eb5959' }"
              @click="$refs.popupSelectVersion.show(list, listVersions)"
              class="cursor-pointer ml-2 mt-1"
              >(Version {{ list.version }} - {{ isNewestVersion ? "Newest" : "Not newest" }})</a
            >
          </vs-row>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>List description</span>
        </div>
        <div class="vx-col sm:w-3/5 w-full">
          <vs-textarea :disabled="!isEditable" v-model="list.description" rows="6"></vs-textarea>
        </div>
      </div>

      <div class="vx-row mb-6" v-if="list.listTypeCategory == 'Other'">
        <div class="vx-col sm:w-1/5 w-full">
          <div>Image url</div>
          <small>The list image in workspace</small>
        </div>
        <div class="vx-col sm:w-3/5 w-full">
          <vs-input :disabled="!isEditable" v-model="list.imageUrl" class="w-full"></vs-input>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <div>IDAM permission</div>
          <small>To view and submit in app</small>
        </div>
        <div class="vx-col sm:w-3/5 w-full">
          <vs-input :disabled="!isEditable" v-model="list.idamPermissionViewInApp" class="w-full"></vs-input>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <div>IDAM permission</div>
          <small>To view submissions in workspace</small>
        </div>
        <div class="vx-col sm:w-3/5 w-full">
          <vs-input :disabled="!isEditable" v-model="list.idamPermissionViewSubmissions" class="w-full"></vs-input>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <div>Venue Filter</div>
          <small>Restrict to specific venues (empty = all venues can see)</small>
        </div>
        <div class="vx-col sm:w-3/5 w-full">
          <VenueSelector 
            :deactivated="!isEditable" 
            v-model="list.venueIds" 
            :multiple="true"
            :filterCountries="listCountryName ? [listCountryName] : []"
          />
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Last updated</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <span>{{ list.savedAction.timestamp | formatDate }} by {{ list.savedAction.userDisplayName }}</span>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>List type</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <span
            ><code>{{ list.type }}</code></span
          >
        </div>
      </div>

      <div class="vx-row mb-6" v-if="list.type != 'CompetitorVisit'">
        <div class="vx-col sm:w-1/5 w-full">
          <span>List country</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <span
            ><code>{{ list.country }}</code></span
          >
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>This version is</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <span
            ><code>{{ list.isPublished ? "Published" : "Not published" }}</code></span
          >
        </div>
      </div>

      <div class="vx-row mb-6" v-if="list.isPublished">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Published at</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <span>{{ list.publishedAction.timestamp | formatDate }}</span> by
          {{ list.publishedAction.userDisplayName }}
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Versions</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <span
            ><code>{{ listVersions.length }} versions</code></span
          >
        </div>
      </div>

      <!-- Save & Remove Button -->
      <vs-row vs-justify="space-between" class="flex flex-wrap items-center mt-6">
        <vs-button color="warning" v-if="isEditable && hasPermission('vp.life.lists.publish')" @click="$emit('publish')">Publish</vs-button>
        <vs-button v-else-if="isNewestVersion && hasPermission('vp.life.lists.edit')" @click="$emit('createNewVersion')"
          >Create new version</vs-button
        >
        <vs-button v-else @click="$emit('routeToNewest')">Go to newest version</vs-button>
        <vs-button
          color="warning"
          v-if="!isNewestVersion && hasPermission('vp.life.lists.publish')"
          @click="revertPromptActive = true"
          >Revert to this version</vs-button
        >
        <vs-button v-if="isNewestVersion && hasPermission('vp.life.lists.edit')" @click="createCopyPromptActive = true"
          >Create copy of list</vs-button
        >
        <vs-button color="success" v-if="isEditable" @click="$emit('update:list')">Save</vs-button>
      </vs-row>
    </vx-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import PopupSelectVersion from "./Popups/PopupSelectVersion.vue";
import VenueSelector from "@/components/VenueSelector.vue";

export default {
  components: { PopupSelectVersion, VenueSelector },
  props: {
    list: {
      type: Object,
      default: () => ({})
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    isNewestVersion: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      createCopyPromptActive: false,
      revertPromptActive: false,

      newListType: this.list.type,
      newListTitle: this.list.title,
      newListCountry: this.list.country
    };
  },
  methods: {
    onClickCopy() {
      this.createCopyPromptActive = true;
    },
    async createCopy() {
      try {
        this.$vs.loading();
        console.log("Creating copy of list", this.list);
        var newListId = await this.$store.dispatch("lists/createCopy", {
          copyListId: this.list.id,
          newListType: this.newListType,
          newListTitle: this.newListTitle,
          newListCountry: this.newListCountry
        });
        this.$vs.notify({
          color: "success",
          title: "Copy",
          text: "Succesfully created copy of list"
        });

        // Route to lists
        this.$router.push({ name: "manage-lists" });
      } catch (err) {
        console.error(err);

        this.$vs.notify({
          color: "danger",
          title: "Could not create copy",
          text: err
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    async revertToThisVersion() {
      try {
        this.$vs.loading();
        const newListId = await this.$store.dispatch("lists/revertToVersion", this.list.id);
        this.$vs.notify({
          color: "success",
          title: "Reverted",
          text: `Created and published a new version from version ${this.list.version}`
        });

        // Route to the newly created, now-active version
        this.$router.push({ name: "manage-list", params: { id: newListId } }).catch(() => {});
      } catch (err) {
        console.error(err);

        this.$vs.notify({
          color: "danger",
          title: "Could not revert",
          text: (err && err.response && err.response.data) || err
        });
      } finally {
        this.$vs.loading.close();
      }
    }
  },
  computed: {
    ...mapGetters({
      listVersions: "lists/listsByType",
      isLoading: "lists/isLoading",
      countries: "venues/countries"
    }),
    newestVersion() {
      if (!this.listVersions || this.listVersions.length === 0) return this.list.version;
      return Math.max(...this.listVersions.map(v => v.version));
    },
    listCountryName() {
      if (!this.list || !this.list.country) return null;
      const country = this.countries.find(c => c.id.toUpperCase() === this.list.country.toUpperCase());
      return country ? country.name : null;
    }
  }
};
</script>
